import { useEffect, useState } from "react";
import { UserButton } from "@clerk/react";
import { Building2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { errMsg, useApi } from "@/lib/api";

export default function Onboarding() {
  const api = useApi();
  const { me, loadMe } = useApp();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const accept = async (raw) => {
    const token = (raw.match(/invite=([\w-]+)/) || [null, raw.trim()])[1];
    try {
      await api.post(`/auth/invite/${token}/accept`);
      localStorage.removeItem("pending_invite");
      window.location.assign("/");
    } catch (e) {
      localStorage.removeItem("pending_invite");
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    const pending = localStorage.getItem("pending_invite");
    if (pending) accept(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/firms/signup", { name });
      await loadMe();
      window.location.assign("/billing");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F4EE] p-6">
      <div className="max-w-3xl w-full fade-up">
        <div className="flex items-center justify-between">
          <div className="small-label">Signed in as {me?.email}</div>
          <UserButton />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-6">Welcome to AVORAlogic</h1>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="panel p-8" data-testid="onboarding-create">
            <Building2 className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
            <div className="font-serif text-2xl mt-4">Start a new firm</div>
            <p className="text-slate-600 text-sm mt-2">You become the firm's admin. Subscribe to start uploading month-end spreadsheets.</p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Firm or family office name" className="mt-6 rounded-sm bg-white" data-testid="onboarding-firm-name-input" />
            <Button onClick={create} disabled={busy || name.trim().length < 2} className="mt-3 w-full rounded-sm" data-testid="onboarding-create-button">
              {busy ? "Creating…" : "Create firm"}
            </Button>
          </div>
          <div className="panel p-8" data-testid="onboarding-invite">
            <Clock className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
            <div className="font-serif text-2xl mt-4">Joining an existing firm?</div>
            <p className="text-slate-600 text-sm mt-2">Paste the invite link you were sent for <b>{me?.email}</b>.</p>
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…/sign-up?invite=…" className="mt-6 rounded-sm bg-white" data-testid="onboarding-invite-link-input" />
            <Button variant="outline" onClick={() => accept(link)} disabled={link.trim().length < 10} className="mt-3 w-full rounded-sm" data-testid="onboarding-accept-invite-button">Join firm</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
